import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import './GanttView.css';
import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { axiosInstance } from "../App";

function GanttView({ tasks, users, onTaskClick, onTasksUpdate }) {
  const [viewMode, setViewMode] = useState(ViewMode.Day);

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || "Atanmamış";
  };

  // Transform tasks to Gantt format - ONE task = ONE bar with estimated shown as dashed outline
  const ganttTasks = [];
  
  tasks.forEach((task) => {
    // Only show tasks with estimated dates or actual dates
    const hasEstimated = task.estimated_start_date && task.estimated_end_date;
    const hasActual = task.actual_start_date && task.actual_end_date;
    const hasDueDate = task.due_date; // Backward compatibility
    
    if (!hasEstimated && !hasActual && !hasDueDate) {
      return; // Skip tasks without dates
    }

    // Get dependencies
    const taskDependencies = task.dependencies || [];
    const validDependencies = taskDependencies.filter(depId => 
      tasks.find(t => t.id === depId)
    );

    // Determine which dates to use for the main bar
    let startDate, endDate;
    let isActualDate = false;
    
    if (hasActual) {
      // Use actual dates if available
      startDate = new Date(task.actual_start_date);
      endDate = new Date(task.actual_end_date);
      isActualDate = true;
    } else if (hasEstimated) {
      // Use estimated dates
      startDate = new Date(task.estimated_start_date);
      endDate = new Date(task.estimated_end_date);
    } else if (hasDueDate) {
      // Fallback to due_date for backward compatibility
      startDate = new Date(task.created_at);
      endDate = new Date(task.due_date);
    }
    
    // Ensure end date is after start date
    if (endDate && endDate <= startDate) {
      endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 1);
    }

    // Determine color based on status
    let barColor = '#3B82F6'; // Blue for todo
    if (task.status === 'done') {
      barColor = '#10B981'; // Green for done
    } else if (task.status === 'in-progress') {
      barColor = '#F59E0B'; // Orange for in-progress
    }

    // Calculate progress
    let progress = 0;
    if (task.status === 'done') {
      progress = 100;
    } else if (task.status === 'in-progress') {
      progress = 50;
    }

    if (endDate) {
      // Create task bar with estimated dates stored for custom rendering
      const ganttTask = {
        id: task.id,
        name: task.title,
        start: startDate,
        end: endDate,
        progress: progress,
        type: 'task',
        styles: {
          backgroundColor: barColor,
          backgroundSelectedColor: barColor,
          progressColor: '#059669',
          progressSelectedColor: '#047857'
        },
        dependencies: validDependencies,
        project: task.assignee_id ? getUserName(task.assignee_id) : 'Atanmamış',
        isDisabled: false,
        // Store estimated dates for custom CSS rendering
        estimatedStart: hasEstimated ? task.estimated_start_date : null,
        estimatedEnd: hasEstimated ? task.estimated_end_date : null,
        isActual: isActualDate
      };
      
      ganttTasks.push(ganttTask);
    }
  });

  const handleTaskClick = (ganttTask) => {
    // Remove -estimated suffix if exists
    const taskId = ganttTask.id.replace('-estimated', '');
    const originalTask = tasks.find(t => t.id === taskId);
    if (originalTask) {
      onTaskClick(originalTask);
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  // Handle task date change via drag & drop
  const handleTaskChange = async (task) => {
    try {
      const originalTask = tasks.find(t => t.id === task.id);
      if (!originalTask) return;

      // Determine which dates to update based on task status
      const updateData = {};
      
      if (originalTask.status === 'done') {
        // For completed tasks, update actual dates
        updateData.actual_start_date = task.start.toISOString();
        updateData.actual_end_date = task.end.toISOString();
      } else {
        // For pending tasks, update estimated dates
        updateData.estimated_start_date = task.start.toISOString();
        updateData.estimated_end_date = task.end.toISOString();
      }

      // Update the task
      await axiosInstance.put(`/tasks/${task.id}`, updateData);
      
      // Update dependent tasks recursively
      await updateDependentTasks(task.id, task.end);
      
      toast.success("Görev tarihi güncellendi");
      
      // Wait a bit for backend to process, then refresh
      setTimeout(() => {
        if (onTasksUpdate) {
          onTasksUpdate();
        }
      }, 500);
    } catch (error) {
      toast.error("Tarih güncellenirken hata oluştu");
      console.error("Task date update error:", error);
      // Refresh on error to revert changes
      if (onTasksUpdate) {
        onTasksUpdate();
      }
    }
  };

  // Update dependent tasks recursively
  const updateDependentTasks = async (taskId, newEndDate) => {
    // Find tasks that depend on this task
    const dependentTasks = tasks.filter(t => 
      t.dependencies && t.dependencies.includes(taskId)
    );

    for (const depTask of dependentTasks) {
      try {
        // Calculate new start date (1 day after parent task end)
        const newStart = new Date(newEndDate);
        newStart.setDate(newStart.getDate() + 1);
        
        // Calculate duration
        const oldStart = new Date(depTask.estimated_start_date || depTask.created_at);
        const oldEnd = new Date(depTask.estimated_end_date || depTask.due_date);
        const durationMs = oldEnd - oldStart;
        
        // Calculate new end date
        const newEnd = new Date(newStart.getTime() + durationMs);
        
        const updateData = {};
        if (depTask.status === 'done') {
          updateData.actual_start_date = newStart.toISOString();
          updateData.actual_end_date = newEnd.toISOString();
        } else {
          updateData.estimated_start_date = newStart.toISOString();
          updateData.estimated_end_date = newEnd.toISOString();
        }
        
        await axiosInstance.put(`/tasks/${depTask.id}`, updateData);
        
        // Recursively update tasks that depend on this one
        await updateDependentTasks(depTask.id, newEnd);
      } catch (error) {
        console.error("Error updating dependent task:", error);
      }
    }
  };

  // Handle progress change
  const handleProgressChange = async (task) => {
    try {
      const newProgress = Math.round(task.progress);
      let newStatus = 'todo';
      
      if (newProgress === 100) {
        newStatus = 'done';
      } else if (newProgress > 0) {
        newStatus = 'in-progress';
      }
      
      await axiosInstance.put(`/tasks/${task.id}`, { 
        status: newStatus 
      });
      
      toast.success("Görev durumu güncellendi");
      
      if (onTasksUpdate) {
        onTasksUpdate();
      }
    } catch (error) {
      toast.error("Durum güncellenirken hata oluştu");
    }
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">Görünüm:</span>
          <div className="flex gap-2">
            <Button
              variant={viewMode === ViewMode.Day ? "default" : "outline"}
              size="sm"
              onClick={() => handleViewModeChange(ViewMode.Day)}
            >
              Gün
            </Button>
            <Button
              variant={viewMode === ViewMode.Week ? "default" : "outline"}
              size="sm"
              onClick={() => handleViewModeChange(ViewMode.Week)}
            >
              Hafta
            </Button>
            <Button
              variant={viewMode === ViewMode.Month ? "default" : "outline"}
              size="sm"
              onClick={() => handleViewModeChange(ViewMode.Month)}
            >
              Ay
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <span className="font-semibold">Durum:</span>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded" style={{ backgroundColor: '#3B82F6' }}></div>
            <span>Yapılacak</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded" style={{ backgroundColor: '#F59E0B' }}></div>
            <span>Devam Ediyor</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded" style={{ backgroundColor: '#10B981' }}></div>
            <span>Tamamlandı</span>
          </div>
          <span className="mx-2">|</span>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded border-2 border-dashed border-black" style={{ backgroundColor: 'transparent' }}></div>
            <span>Tahmini Tarih (kesikli çerçeve)</span>
          </div>
        </div>
      </div>

      {/* Gantt Chart */}
      {ganttTasks.length === 0 ? (
        <div className="text-center py-12 text-gray-500 border rounded-lg bg-white">
          Teslim tarihi olan görev bulunamadı
        </div>
      ) : (
        <div className="border rounded-lg overflow-auto bg-white" style={{ minHeight: '500px' }}>
          <Gantt
            tasks={ganttTasks}
            viewMode={viewMode}
            onDateChange={handleTaskChange}
            onProgressChange={handleProgressChange}
            locale="tr"
            listCellWidth="155px"
            columnWidth={viewMode === ViewMode.Month ? 300 : viewMode === ViewMode.Week ? 250 : 65}
            todayColor="rgba(252, 211, 77, 0.3)"
            arrowColor="#94a3b8"
            arrowIndent={20}
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="14px"
            rowHeight={50}
            barCornerRadius={3}
            handleWidth={8}
            timeStep={300000}
            TooltipContent={({ task }) => {
              const originalTask = tasks.find(t => t.id === task.id);
              const statusLabels = {
                "todo": "Yapılacak",
                "in-progress": "Devam Ediyor",
                "done": "Tamamlandı"
              };
              const priorityLabels = {
                "low": "Düşük",
                "medium": "Orta",
                "high": "Yüksek"
              };
              
              // Get dependency task names
              const dependencyNames = (originalTask?.dependencies || [])
                .map(depId => {
                  const depTask = tasks.find(t => t.id === depId);
                  return depTask?.title;
                })
                .filter(Boolean);
              
              // Format dates
              const formatDate = (dateStr) => {
                if (!dateStr) return null;
                const date = new Date(dateStr);
                return date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'short', day: 'numeric' });
              };

              const hasEstimated = originalTask?.estimated_start_date && originalTask?.estimated_end_date;
              const hasActual = originalTask?.actual_start_date && originalTask?.actual_end_date;

              return (
                <div className="p-3 bg-gray-900 text-white rounded shadow-lg text-sm max-w-xs">
                  <div className="font-semibold text-base mb-2">{task.name}</div>
                  {originalTask?.description && (
                    <div className="text-gray-300 mb-2 text-xs line-clamp-2">{originalTask.description}</div>
                  )}
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Atanan:</span>
                      <span>{task.project}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Durum:</span>
                      <span>{statusLabels[originalTask?.status] || 'Yapılacak'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Öncelik:</span>
                      <span>{priorityLabels[originalTask?.priority] || 'Orta'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">İlerleme:</span>
                      <span>{task.progress}%</span>
                    </div>
                    
                    {/* Date information */}
                    {hasEstimated && (
                      <div className="pt-2 mt-2 border-t border-gray-700">
                        <div className="text-yellow-400 font-semibold mb-1">📅 Tahmini Tarihler:</div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Başlangıç:</span>
                          <span>{formatDate(originalTask.estimated_start_date)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Bitiş:</span>
                          <span>{formatDate(originalTask.estimated_end_date)}</span>
                        </div>
                      </div>
                    )}
                    
                    {hasActual && (
                      <div className="pt-2 mt-2 border-t border-gray-700">
                        <div className="text-green-400 font-semibold mb-1">✅ Gerçekleşen Tarihler:</div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Başlangıç:</span>
                          <span>{formatDate(originalTask.actual_start_date)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Bitiş:</span>
                          <span>{formatDate(originalTask.actual_end_date)}</span>
                        </div>
                      </div>
                    )}
                    
                    {dependencyNames.length > 0 && (
                      <div className="pt-2 mt-2 border-t border-gray-700">
                        <div className="text-gray-400 mb-1">🔗 Bağımlı Olduğu Görevler:</div>
                        <div className="space-y-1">
                          {dependencyNames.map((name, idx) => (
                            <div key={idx} className="text-xs text-gray-300">• {name}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            }}
          />
        </div>
      )}

      {/* Info */}
      <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
        💡 <strong>İpucu:</strong> Gantt grafiğinde her görev için <strong>gerçekleşen tarihler</strong> (varsa) renkli çubuk olarak gösterilir.
        Tahmini tarihler tooltip'te (fare ile üzerine gelince) görülebilir. Kesikli siyah çerçeve tahmini tarihi, 
        düz renkli çubuk gerçekleşen tarihi temsil eder. Mavi renk yapılacak, turuncu devam eden, 
        yeşil tamamlanan görevleri temsil eder. Ok işaretleri görevler arası bağımlılıkları gösterir.
      </div>
    </div>
  );
}

export default GanttView;
